/**
 * Blog Editor Component
 * 
 * Complete blog post editor with form fields, Quill editor, image upload, and SEO fields
 */

import { useState, useEffect } from 'react';
import { X, Save, Eye, Globe } from 'lucide-react';
import QuillEditor from './QuillEditor';
import ImageUploader from './ImageUploader';
import { generateSlug, generateMetaDescription, extractKeywords } from '../../utils/seoUtils';

/**
 * Blog Editor Component
 * @param {Object} props
 * @param {Object} props.blog - Existing blog data (for editing)
 * @param {Function} props.onSave - Callback when blog is saved (blogData) => void
 * @param {Function} props.onCancel - Callback when editor is cancelled
 * @param {string} props.userId - User ID for image uploads
 */
export default function BlogEditor({ blog, onSave, onCancel, userId }) {
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    category: '',
    tags: [],
    header_image_url: '',
    meta_title: '',
    meta_description: '',
    meta_keywords: [],
    is_published: false,
  });

  const [tagInput, setTagInput] = useState('');
  const [autoGenerateSEO, setAutoGenerateSEO] = useState(true);
  const [errors, setErrors] = useState({});

  // Initialize form data when blog prop changes
  useEffect(() => {
    if (blog) {
      setFormData({
        title: blog.title || '',
        slug: blog.slug || '',
        excerpt: blog.excerpt || '',
        content: blog.content || '',
        category: blog.category || '',
        tags: Array.isArray(blog.tags) ? blog.tags : [],
        header_image_url: blog.header_image_url || '',
        meta_title: blog.meta_title || blog.title || '',
        meta_description: blog.meta_description || '',
        meta_keywords: Array.isArray(blog.meta_keywords) ? blog.meta_keywords : [],
        is_published: blog.is_published || false,
      });
    }
  }, [blog]);

  // Auto-generate slug from title
  useEffect(() => {
    if (formData.title && !blog?.slug) {
      const slug = generateSlug(formData.title);
      setFormData((prev) => ({ ...prev, slug }));
    }
  }, [formData.title, blog?.slug]);

  // Auto-generate SEO fields when content changes
  useEffect(() => {
    if (autoGenerateSEO && formData.content) {
      const metaDesc = generateMetaDescription(formData.content);
      const keywords = extractKeywords(formData.content);
      
      setFormData((prev) => ({
        ...prev,
        meta_description: prev.meta_description || metaDesc,
        meta_keywords: prev.meta_keywords.length === 0 ? keywords : prev.meta_keywords,
      }));
    }
  }, [formData.content, autoGenerateSEO]);

  // Auto-generate meta title from title
  useEffect(() => {
    if (formData.title && !formData.meta_title) {
      setFormData((prev) => ({ ...prev, meta_title: prev.title }));
    }
  }, [formData.title]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleAddTag = () => {
    const tag = tagInput.trim();
    if (tag && !formData.tags.includes(tag)) {
      handleChange('tags', [...formData.tags, tag]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    handleChange(
      'tags',
      formData.tags.filter((tag) => tag !== tagToRemove)
    );
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.slug.trim()) {
      newErrors.slug = 'Slug is required';
    }

    if (!formData.content.trim()) {
      newErrors.content = 'Content is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = (publish = false) => {
    if (!validate()) {
      return;
    }

    const blogData = {
      ...formData,
      is_published: publish || formData.is_published,
    };

    onSave(blogData);
  };

  const categories = [
    { value: 'training', label: 'Training' },
    { value: 'nutrition', label: 'Nutrition' },
    { value: 'lifestyle', label: 'Lifestyle' },
  ];

  return (
    <div className="blog-editor max-w-5xl mx-auto bg-white rounded-lg shadow-lg">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-gray-200">
        <h2 className="text-2xl font-bold text-gray-900">
          {blog ? 'Edit Blog Post' : 'Create New Blog Post'}
        </h2>
        <button
          type="button"
          onClick={onCancel}
          className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Form */}
      <div className="p-6 space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto">
        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Title <span className="text-red-600">*</span>
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => handleChange('title', e.target.value)}
            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
              errors.title ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Enter blog post title"
          />
          {errors.title && (
            <p className="mt-1 text-sm text-red-600">{errors.title}</p>
          )}
        </div>

        {/* Slug */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Slug <span className="text-red-600">*</span>
          </label>
          <input
            type="text"
            value={formData.slug}
            onChange={(e) => handleChange('slug', generateSlug(e.target.value))}
            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
              errors.slug ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="url-friendly-slug"
          />
          {errors.slug && (
            <p className="mt-1 text-sm text-red-600">{errors.slug}</p>
          )}
          <p className="mt-1 text-xs text-gray-500">
            URL: /blog/{formData.slug || 'your-slug'}
          </p>
        </div>

        {/* Category and Tags */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category
            </label>
            <select
              value={formData.category}
              onChange={(e) => handleChange('category', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Select category</option>
              {categories.map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tags
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={handleKeyPress}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Add tag and press Enter"
              />
              <button
                type="button"
                onClick={handleAddTag}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Add
              </button>
            </div>
            {formData.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="hover:text-primary-900"
                      aria-label={`Remove ${tag}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Excerpt */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Excerpt
          </label>
          <textarea
            value={formData.excerpt}
            onChange={(e) => handleChange('excerpt', e.target.value)}
            rows={3}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="Short summary for preview cards"
          />
          <p className="mt-1 text-xs text-gray-500">
            {formData.excerpt.length}/300 characters
          </p>
        </div>

        {/* Header Image */}
        <ImageUploader
          value={formData.header_image_url}
          onChange={(url) => handleChange('header_image_url', url)}
          blogId={blog?.id || 'temp'}
          userId={userId}
          label="Header Image (Optional)"
        />

        {/* Content Editor */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Content <span className="text-red-600">*</span>
          </label>
          <QuillEditor
            value={formData.content}
            onChange={(content) => handleChange('content', content)}
            placeholder="Start writing your blog post..."
            minHeight={400}
          />
          {errors.content && (
            <p className="mt-1 text-sm text-red-600">{errors.content}</p>
          )}
        </div>

        {/* SEO Section */}
        <div className="border-t border-gray-200 pt-6">
          <div className="flex items-center gap-2 mb-4">
            <Globe className="w-5 h-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-900">SEO Settings</h3>
            <label className="ml-auto flex items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={autoGenerateSEO}
                onChange={(e) => setAutoGenerateSEO(e.target.checked)}
                className="rounded"
              />
              Auto-generate
            </label>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Meta Title
              </label>
              <input
                type="text"
                value={formData.meta_title}
                onChange={(e) => handleChange('meta_title', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="SEO title (defaults to blog title)"
              />
              <p className="mt-1 text-xs text-gray-500">
                {formData.meta_title.length}/60 characters
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Meta Description
              </label>
              <textarea
                value={formData.meta_description}
                onChange={(e) => handleChange('meta_description', e.target.value)}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="SEO description (auto-generated from content)"
              />
              <p className="mt-1 text-xs text-gray-500">
                {formData.meta_description.length}/160 characters
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Meta Keywords
              </label>
              <input
                type="text"
                value={formData.meta_keywords.join(', ')}
                onChange={(e) => {
                  const keywords = e.target.value
                    .split(',')
                    .map((k) => k.trim())
                    .filter((k) => k.length > 0);
                  handleChange('meta_keywords', keywords);
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="keyword1, keyword2, keyword3"
              />
              <p className="mt-1 text-xs text-gray-500">
                Comma-separated keywords (auto-extracted from content)
              </p>
            </div>
          </div>
        </div>

        {/* Publish Toggle */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="is_published"
            checked={formData.is_published}
            onChange={(e) => handleChange('is_published', e.target.checked)}
            className="rounded"
          />
          <label htmlFor="is_published" className="text-sm font-medium text-gray-700">
            Publish immediately
          </label>
        </div>
      </div>

      {/* Footer Actions */}
      <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => handleSave(false)}
          className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
        >
          <Save className="w-4 h-4" />
          Save Draft
        </button>
        <button
          type="button"
          onClick={() => handleSave(true)}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
        >
          <Eye className="w-4 h-4" />
          {formData.is_published ? 'Update' : 'Publish'}
        </button>
      </div>
    </div>
  );
}
